'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { NavSectionProps } from '@/components/nav-section';

/**
 * Translates navigation menu items on the client side.
 * This ensures that menu labels update when the language changes.
 */
export function useTranslatedNavData(navData: NavSectionProps['data']): NavSectionProps['data'] {
  const { t, i18n } = useTranslation('navbar');

  const menuMap = useMemo(
    () => t('menu', { returnObjects: true }) as Record<string, string>,
    [t, i18n.language]
  );

  return useMemo(() => {
    if (!navData || navData.length === 0) return navData;

    const translateLabel = (title: string | undefined): string => {
      if (!title) return '';
      const normalized = title.toLowerCase().replace(/\s+/g, '.');

      // Try direct translation key first
      const key = `menu.${normalized}`;
      const translated = t(key);
      if (translated !== key) return translated;

      // Try menu map with dotted notation
      if (menuMap?.[normalized]) {
        return menuMap[normalized];
      }

      return title;
    };

    const translateItem = (item: any): any => {
      return {
        ...item,
        title: translateLabel(item.title),
        children: item.children ? item.children.map(translateItem) : undefined,
      };
    };

    return navData.map((group) => ({
      ...group,
      items: group.items.map(translateItem),
    }));
  }, [navData, t, menuMap, i18n.language]);
}

